/**
 * Persistent device identification and metadata.
 * device_id is stored in localStorage and reused; never exposed in URL or regenerated per load.
 */

import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';
import type { DeviceMeta, DeviceType } from './types';

const DEVICE_ID_KEY = 'observability_device_id';

function getOrientation(): 'portrait' | 'landscape' | 'unknown' {
  if (typeof window === 'undefined') return 'unknown';
  try {
    if (window.screen?.orientation?.type) {
      const t = window.screen.orientation.type;
      if (t.includes('portrait')) return 'portrait';
      if (t.includes('landscape')) return 'landscape';
    }
    if (typeof window.matchMedia === 'function') {
      if (window.matchMedia('(orientation: portrait)').matches) return 'portrait';
      if (window.matchMedia('(orientation: landscape)').matches) return 'landscape';
    }
  } catch {
    // ignore
  }
  return 'unknown';
}

/**
 * iPadOS 13+ reports as Mac in UA; detect iPad by touch + UA patterns.
 */
function resolveDeviceType(parser: UAParser): DeviceType {
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const ua = nav.userAgent || '';
  const hasTouch = nav.maxTouchPoints != null && nav.maxTouchPoints > 1;
  const looksLikeMac = /Mac/i.test(ua);
  const hasIPadInUA = /iPad/i.test(ua);

  if (hasIPadInUA) return 'iPad';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (looksLikeMac && hasTouch) return 'iPad'; // iPadOS 13+ often reports as Mac with touch
  if (parser.getDevice().type === 'tablet') return 'iPad';
  if (parser.getDevice().type === 'mobile') return 'iPhone';
  return 'desktop';
}

let deviceMetaSnapshot: DeviceMeta | null = null;

function buildDeviceMeta(deviceId: string): DeviceMeta {
  const parser = new UAParser();
  const browser = parser.getBrowser();
  const os = parser.getOS();
  const device = parser.getDevice();

  const screenWidth = typeof window !== 'undefined' ? window.screen?.width ?? 0 : 0;
  const screenHeight = typeof window !== 'undefined' ? window.screen?.height ?? 0 : 0;
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio ?? 1 : 1;

  return {
    device_id: deviceId,
    device_type: resolveDeviceType(parser),
    os: os.name || 'unknown',
    os_version: os.version || 'unknown',
    browser: browser.name || 'unknown',
    browser_version: browser.version || 'unknown',
    screen_width: screenWidth,
    screen_height: screenHeight,
    device_pixel_ratio: dpr,
    orientation: getOrientation(),
  };
}

/**
 * Returns a persistent device_id (UUID v4). Stored in localStorage; reused on future sessions.
 * Never read from or write to URL. Safe to call before any async init.
 */
export function getDeviceId(): string {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return 'unknown';
  }
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id || id.length < 30) {
      id = uuidv4();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return 'unknown';
  }
}

/**
 * Returns device metadata snapshot (read-only). Captured once at first call.
 * Safe to call before any async init.
 */
export function getDeviceMeta(): DeviceMeta {
  if (deviceMetaSnapshot) return deviceMetaSnapshot;
  const deviceId = getDeviceId();
  deviceMetaSnapshot = buildDeviceMeta(deviceId);
  return deviceMetaSnapshot;
}
